/**
 * Sprint BG-DataSettings 19 (U-P12) — user app の i18n エントリポイント。
 *
 * 設計方針:
 *   - `@citizen-walk/shared-i18n` の軽量 `t()` / `setLocale()` を base に、user app 用に
 *     localStorage 永続化 (`cw.locale`) + React reactive 再 render (`useLocale` / `useT`) を追加。
 *   - 旧 v1 (`old/apps/user/src/main.tsx`) では i18next を直接 setup していたが、v2 では
 *     shared-i18n の依存無し translator を使うため react-i18next は導入しない。同等の表面
 *     API として `t(key, params?)` / `useT()` (hook) を提供。
 *   - 旧の `useTranslation('user')` namespace は v2 では key prefix で吸収 (例: `offline_map.*`)。
 *
 * localStorage `cw.locale`:
 *   - `'ja'` / `'en'` のみ受理、未設定 / 不正値は `'ja'` fallback。
 *   - `setLocaleAndPersist()` は同期で localStorage に書込 + listener 通知。
 *   - SSR は v2 で未サポート (PWA client-only) のため `window` ガード簡略化。
 */

import {
  setLocale as setSharedLocale,
  t as sharedT,
  type I18nKey,
  type Locale,
} from '@citizen-walk/shared-i18n';
import { useSyncExternalStore } from 'react';

export { t } from '@citizen-walk/shared-i18n';
export type { I18nKey, Locale };

const STORAGE_KEY = 'cw.locale';

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  for (const l of listeners) l();
}

/**
 * 永続化された locale を読込。不正値は 'ja' fallback (shared-i18n の DEFAULT_LOCALE と一致)。
 */
function readPersistedLocale(): Locale {
  if (typeof localStorage === 'undefined') return 'ja';
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'en' || raw === 'ja') return raw;
  } catch {
    /* localStorage 不可 (private mode / SSR / quota) は ja */
  }
  return 'ja';
}

let current: Locale = 'ja';

/**
 * アプリ起動時 (main.tsx) で 1 回呼ぶ。
 * localStorage から locale を読込 → shared-i18n に反映 → cross-tab storage event 購読。
 */
export function initI18n(): void {
  current = readPersistedLocale();
  setSharedLocale(current);
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      // 他 tab で locale 変更 → 自 tab にも反映 (旧 v1 にはなかったが、PWA 多 tab 想定で追加)
      if (e.key !== STORAGE_KEY) return;
      const next = readPersistedLocale();
      if (next !== current) {
        current = next;
        setSharedLocale(current);
        notify();
      }
    });
  }
}

/** 現在の locale を取得 (non-reactive、hook 外用)。 */
export function getLocale(): Locale {
  return current;
}

/**
 * locale を切替 + 永続化 + 再 render 通知。
 * 未知 locale は no-op (shared-i18n と挙動を揃える)。
 */
export function setLocaleAndPersist(locale: Locale): void {
  if (locale !== 'ja' && locale !== 'en') return;
  if (current === locale) return;
  current = locale;
  setSharedLocale(locale);
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
  notify();
}

/**
 * React hook: 現在の locale を購読 + 切替関数を返す。
 * `useSyncExternalStore` で安全に外部 store と sync (Concurrent React 互換)。
 */
export function useLocale(): {
  locale: Locale;
  setLocale: (locale: Locale) => void;
} {
  const locale = useSyncExternalStore<Locale>(
    (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    () => current,
    () => 'ja' as Locale,
  );
  return { locale, setLocale: setLocaleAndPersist };
}

/**
 * React hook: 翻訳関数 `t()` を返す (locale 切替時に再 render される)。
 * 旧 v1 `useTranslation('user')` 表面 API 互換 (namespace 引数は無視)。
 */
export function useT(): (key: I18nKey, params?: Record<string, string | number>) => string {
  useLocale(); // subscribe so component re-renders on locale change
  return sharedT;
}

/** test 用: listeners / current locale を reset。 */
export function __resetI18n(): void {
  current = 'ja';
  setSharedLocale('ja');
  listeners.clear();
}
